
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { trackSignUp, detectAuthMethod, isNewUser } from '@/utils/analytics';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    
    // Set a maximum loading timeout to prevent infinite loading
    const loadingTimeout = setTimeout(() => {
      if (mounted) {
        console.log('⏰ AuthContext: Loading timeout reached, setting loading to false');
        setLoading(false);
      }
    }, 8000); // 8 second maximum loading time

    // Set up auth state listener FIRST
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (mounted) {
          console.log('🔄 AuthContext: Auth state change:', event, session?.user?.email);
          setSession(session);
          setUser(session?.user ?? null);
          
          // Track sign up for new users
          if (event === 'SIGNED_IN' && session?.user) {
            const user = session.user;
            if (user.created_at && isNewUser(user.created_at)) {
              const method = detectAuthMethod(user);
              trackSignUp(method, user.id);
            }
          }
          
          // Only set loading to false if we're not in a loading state already
          if (loading) {
            setLoading(false);
            clearTimeout(loadingTimeout);
          }
        }
      }
    );

    // THEN check for existing session
    const getInitialSession = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (mounted) {
          if (error) {
            console.error('❌ AuthContext: Error getting initial session:', error);
          } else {
            console.log('🔍 AuthContext: Initial session check:', session?.user?.email);
          }
          
          setSession(session);
          setUser(session?.user ?? null);
          setLoading(false);
          clearTimeout(loadingTimeout);
        }
      } catch (error) {
        if (mounted) {
          console.error('❌ AuthContext: Exception getting initial session:', error);
          setLoading(false);
          clearTimeout(loadingTimeout);
        }
      }
    };

    getInitialSession();

    return () => {
      mounted = false;
      clearTimeout(loadingTimeout);
      subscription.unsubscribe();
    };
  }, []); // Remove loading from dependency array to prevent re-runs

  const signOut = async () => {
    await supabase.auth.signOut();
  };

  return (
    <AuthContext.Provider value={{ user, session, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
